import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { Wrench } from 'lucide-react';

export const READINESS_META = {
  green: { label: 'Ready', dot: '#16A34A', bg: '#ECFDF3', border: '#ACE5C0', text: '#166534' },
  amber: { label: 'Attention', dot: '#D97706', bg: '#FFFBEB', border: '#F2D97A', text: '#92600A' },
  red: { label: 'Not ready', dot: '#DC2626', bg: '#FEF2F2', border: '#F6B4AC', text: '#991B1B' },
  grey: { label: 'Not assessed', dot: '#9A958A', bg: '#F4F2EE', border: '#DEDAD1', text: '#6B665D' },
};

// Compact coloured dot for list rows. Hover shows the reasons.
export const ReadinessDot = ({ readiness }) => {
  const status = readiness?.status || 'grey';
  const meta = READINESS_META[status] || READINESS_META.grey;
  const reasons = readiness?.reasons || [];
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center justify-center w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white"
            style={{ background: meta.dot }}
            data-testid={`readiness-dot-${status}`}
            aria-label={`Readiness: ${meta.label}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="font-semibold mb-0.5">{meta.label}</div>
          <ul className="text-xs list-disc pl-4 space-y-0.5">
            {reasons.slice(0, 6).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Full pill for the detail drawer.
// Linked-gear warning for a Main asset whose auxiliaries aren't all Green.
export const GearChip = ({ gear }) => {
  if (!gear || !gear.worst) return null;
  const meta = READINESS_META[gear.worst] || READINESS_META.amber;
  const count = (gear.red || 0) + (gear.amber || 0);
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border flex-shrink-0"
            style={{ background: meta.bg, borderColor: meta.border, color: meta.text }}
            data-testid="gear-warning"
          >
            <Wrench className="w-3 h-3" />
            {count}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="font-semibold mb-0.5">Linked gear needs attention</div>
          <ul className="text-xs list-disc pl-4 space-y-0.5">
            {gear.items.map((it, i) => (
              <li key={i}>{it.name} — {READINESS_META[it.status]?.label || it.status}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ReadinessBadge = ({ readiness, className = '' }) => {
  const status = readiness?.status || 'grey';
  const meta = READINESS_META[status] || READINESS_META.grey;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[11px] font-bold uppercase tracking-[0.08em] border ${className}`}
      style={{ background: meta.bg, borderColor: meta.border, color: meta.text }}
      data-testid={`readiness-badge-${status}`}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
};
